import { oc } from "@orpc/contract";
import { z } from "zod";

/**
 * Scalar helpers
 */
const UUIDSchema = z.string().uuid();

/**
 * Date-only format validation (YYYY-MM-DD)
 */
const DateOnlySchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, "Date must be in YYYY-MM-DD format");

/**
 * Common attachment schema with URL validation
 */
const AttachmentUrlSchema = z.string().url().nullable().optional();

/**
 * Discount type enum matching backend model
 */
export const DiscountTypeSchema = z.enum(["PERCENT", "FIXED_PRICE"]);

/**
 * Discount target type enum matching backend model
 */
export const DiscountTargetTypeSchema = z.enum([
  "SKU",
  "PRODUCT_DISCOUNT_GROUP",
  "ALL_PRODUCTS",
  "PRODUCT_CATEGORY",
  "PRODUCT_LINE",
]);

/**
 * Pagination schema
 */
export const DiscountPaginationSchema = z.object({
  total: z.number().int().nonnegative(),
  limit: z.number().int().positive(),
  offset: z.number().int().nonnegative(),
});

/**
 * Filters accepted by the list endpoint
 */
export const ListDiscountsInputSchema = z.object({
  search: z.string().optional(),
  discountType: z.array(DiscountTypeSchema).optional(),
  isActive: z.boolean().optional(),
  effectiveFrom: z.string().optional(),
  effectiveTo: z.string().optional(),
  customerId: UUIDSchema.optional(),
  /** Filter scopes by multiple customer IDs (takes precedence over customerId) */
  customerIds: z.array(UUIDSchema).optional(),
  /** When true, only show discounts scoped to all customers (customerId IS NULL) */
  allCustomersOnly: z.boolean().optional(),
  /** Filter scopes by target type (tab filtering) */
  targetType: DiscountTargetTypeSchema.optional(),
  /** Filter scopes by exact SKU match on product baseSku */
  skuFilter: z.array(z.string()).optional(),
  /** Filter scopes by targetValue for PRODUCT_CATEGORY scopes */
  categoryFilter: z.array(z.string()).optional(),
  /** Filter scopes by targetValue for PRODUCT_LINE scopes */
  lineFilter: z.array(z.string()).optional(),
  /** Filter scopes by exact endCustomerName match */
  endCustomerFilter: z.array(z.string()).optional(),
  limit: z.number().int().min(1).max(5000).optional(),
  offset: z.number().int().min(0).optional(),
});

/**
 * Customer summary for scope display
 */
export const DiscountScopeCustomerSchema = z.object({
  id: UUIDSchema,
  name: z.string(),
});

/**
 * Product summary for scope display
 */
export const DiscountScopeProductSchema = z.object({
  id: UUIDSchema,
  name: z.string().nullable(),
  sku: z.string().nullable(),
  tags: z.array(z.string()).optional(),
});

/**
 * Product discount group summary
 */
export const DiscountScopeProductGroupSchema = z.object({
  id: UUIDSchema,
  name: z.string(),
});

/**
 * Discount scope schema
 */
export const DiscountScopeSchema = z.object({
  id: UUIDSchema,
  discountId: UUIDSchema,
  customerId: UUIDSchema.nullable(),
  targetType: DiscountTargetTypeSchema,
  targetId: UUIDSchema.nullable(),
  targetValue: z.string().nullable().optional(),
  isActive: z.boolean(),
  attachment: z.string().url().nullable(),
  endCustomerName: z.string().nullable().optional(),
  customer: DiscountScopeCustomerSchema.nullable().optional(),
  product: DiscountScopeProductSchema.nullable().optional(),
  productDiscountGroup: DiscountScopeProductGroupSchema.nullable().optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

/**
 * User summary for created/modified by
 */
export const DiscountUserSchema = z.object({
  id: UUIDSchema,
  name: z.string().nullable(),
  email: z.string(),
});

/**
 * Main discount schema
 */
export const DiscountSchema = z.object({
  id: UUIDSchema,
  organizationId: UUIDSchema,
  name: z.string(),
  discountType: DiscountTypeSchema,
  percentBps: z.number().int().nullable(),
  value: z.union([z.number(), z.string()]).nullable(),
  effectiveStart: z.string().nullable(),
  effectiveEnd: z.string().nullable(),
  isActive: z.boolean(),
  poNumber: z.string().nullable(),
  isSingleUse: z.boolean(),
  consumedAt: z.coerce.date().nullable(),
  explanation: z.string().nullable(),
  /**
   * External code identifying what this discount is for (e.g. an SPR / special
   * pricing request). Used to disambiguate when matching incoming PO lines —
   * two discounts can otherwise look identical (same amount, same end customer).
   */
  referenceCode: z.string().nullable(),
  createdByUserId: UUIDSchema.nullable(),
  lastModifiedByUserId: UUIDSchema.nullable(),
  createdBy: DiscountUserSchema.nullable().optional(),
  lastModifiedBy: DiscountUserSchema.nullable().optional(),
  scopes: z.array(DiscountScopeSchema).optional(),
  scopeCount: z.number().int().nonnegative().optional(),
  /** Approval status for multi-level approval workflow */
  approvalStatus: z.enum(["PENDING", "APPROVED", "REJECTED"]).nullable(),
  /** Current approval level (1, 2, etc.) */
  currentApprovalLevel: z.number().nullable(),
  /** Total approval levels required */
  requiredApprovalLevel: z.number().nullable(),
  /**
   * Optional lifetime quantity cap. Null means unbounded.
   * Stored as a string by Sequelize (DECIMAL); the API accepts both for safety.
   */
  maxQuantity: z.union([z.number(), z.string()]).nullable(),
  /**
   * Quantity already consumed by approved sales orders (sum of
   * ar_applied_discounts.consumed_quantity for non-reversed rows). Derived on
   * read; only populated for capped discounts.
   */
  consumedQuantity: z.number().optional(),
  /** Remaining quantity = maxQuantity − consumedQuantity. Null when unbounded. */
  remainingQuantity: z.number().nullable().optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

/**
 * List response schema
 */
export const ListDiscountsResponseSchema = z.object({
  discounts: z.array(DiscountSchema),
  pagination: DiscountPaginationSchema,
});

/**
 * Create discount scope input schema - uses discriminated union to enforce
 * that targetId is required for SKU and PRODUCT_DISCOUNT_GROUP,
 * targetValue is required for PRODUCT_CATEGORY and PRODUCT_LINE,
 * and nothing extra for ALL_PRODUCTS.
 */
export const CreateDiscountScopeInputSchema = z.discriminatedUnion(
  "targetType",
  [
    z.object({
      targetType: z.literal("SKU"),
      customerId: UUIDSchema.nullable().optional(),
      targetId: UUIDSchema, // Required for SKU (productVariantId)
      endCustomerName: z.string().nullable().optional(),
      attachment: AttachmentUrlSchema,
    }),
    z.object({
      targetType: z.literal("PRODUCT_DISCOUNT_GROUP"),
      customerId: UUIDSchema.nullable().optional(),
      targetId: UUIDSchema, // Required for product discount group
      endCustomerName: z.string().nullable().optional(),
      attachment: AttachmentUrlSchema,
    }),
    z.object({
      targetType: z.literal("ALL_PRODUCTS"),
      customerId: UUIDSchema.nullable().optional(),
      targetId: UUIDSchema.nullable().optional(), // Optional for all products
      endCustomerName: z.string().nullable().optional(),
      attachment: AttachmentUrlSchema,
    }),
    z.object({
      targetType: z.literal("PRODUCT_CATEGORY"),
      customerId: UUIDSchema.nullable().optional(),
      targetValue: z.string().min(1, "Category name is required"),
      endCustomerName: z.string().nullable().optional(),
      attachment: AttachmentUrlSchema,
    }),
    z.object({
      targetType: z.literal("PRODUCT_LINE"),
      customerId: UUIDSchema.nullable().optional(),
      targetValue: z.string().min(1, "Product line name is required"),
      endCustomerName: z.string().nullable().optional(),
      attachment: AttachmentUrlSchema,
    }),
  ],
);

/**
 * Create discount input schema with comprehensive validation:
 * - Percent values must be in range [0, 100]
 * - Dates must be in YYYY-MM-DD format
 * - effectiveEnd must be >= effectiveStart when both are present
 */
export const CreateDiscountInputSchema = z
  .object({
    name: z.string().min(1, "Name is required"),
    discountType: DiscountTypeSchema,
    value: z.number().min(0, "Value must be 0 or above"),
    effectiveStart: DateOnlySchema,
    effectiveEnd: DateOnlySchema,
    explanation: z
      .string()
      .min(10, "Explanation must be at least 10 characters"),
    poNumber: z.string().max(255).nullable().optional(),
    /**
     * External code identifying the grant this discount is for (e.g. an SPR
     * code). Input is trimmed; uniqueness is not enforced at the schema
     * level so customers can opt in without backfilling existing rows.
     * Callers that mean "no code" must send `null` or omit the field —
     * empty strings are rejected so we don't silently launder `""` into
     * `null`.
     */
    referenceCode: z.string().trim().min(1).max(255).nullable().optional(),
    isSingleUse: z.boolean().optional(),
    /**
     * Optional lifetime quantity cap. Once set, approved sales orders draw
     * down from a shared pool. Leave null/omitted for an unbounded discount.
     */
    maxQuantity: z
      .number()
      .positive("Max quantity must be greater than zero")
      .nullable()
      .optional(),
    scopes: z
      .array(CreateDiscountScopeInputSchema)
      .min(1, "At least one scope is required"),
    // Optional: If provided, signals the associated workflow to recheck pricing
    stagedPurchaseOrderId: z.string().uuid().optional(),
  })
  .superRefine((data, ctx) => {
    // Validate percent value is <= 100 for PERCENT discount type
    if (data.discountType === "PERCENT" && data.value > 100) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Percent discount must be between 0 and 100",
        path: ["value"],
      });
    }

    // Validate date order: effectiveEnd >= effectiveStart
    if (data.effectiveStart && data.effectiveEnd) {
      const start = new Date(data.effectiveStart);
      const end = new Date(data.effectiveEnd);
      if (end < start) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "End date must be on or after start date",
          path: ["effectiveEnd"],
        });
      }
    }

    // Single-use discounts must be SKU-scoped and customer-specific
    if (data.isSingleUse) {
      const hasNonSkuScope = data.scopes.some((s) => s.targetType !== "SKU");
      if (hasNonSkuScope) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            "Single-use discounts can only be applied to SKU-level scopes",
          path: ["isSingleUse"],
        });
      }
      const hasAllCustomersScope = data.scopes.some((s) => !s.customerId);
      if (hasAllCustomersScope) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Single-use discounts must be scoped to a specific customer",
          path: ["isSingleUse"],
        });
      }
    }
  });

/**
 * Create discount response schema
 */
export const CreateDiscountResponseSchema = z.object({
  discount: DiscountSchema,
});

// ─── List prices ────────────────────────────────────────────────────────────

/**
 * Single SKU list price result
 */
export const ListPriceResultSchema = z.object({
  sku: z.string(),
  listPrice: z.number(),
  currencyCode: z.string().nullable(),
  priceListName: z.string().nullable(),
  uomCode: z.string().nullable(),
});

export const GetListPricesInputSchema = z.object({
  customerId: UUIDSchema,
  skus: z.array(z.string()).min(1).max(500),
});

export const GetListPricesResponseSchema = z.object({
  prices: z.record(z.string(), ListPriceResultSchema.nullable()),
});

// ─── Available categories ───────────────────────────────────────────────────

export const GetAvailableCategoriesResponseSchema = z.object({
  categories: z.array(z.string()),
  productLines: z.array(z.string()),
});

// ─── Product discount groups ────────────────────────────────────────────────

export const ProductDiscountGroupSchema = z.object({
  id: UUIDSchema,
  name: z.string(),
  description: z.string().nullable(),
  itemCount: z.number().int().nonnegative(),
});

export const GetProductDiscountGroupsResponseSchema = z.object({
  groups: z.array(ProductDiscountGroupSchema),
});

// ─── Delete discount ────────────────────────────────────────────────────────

export const DeleteDiscountInputSchema = z.object({
  id: UUIDSchema,
});

export const DeleteDiscountResponseSchema = z.object({
  success: z.boolean(),
});

// ─── Update max quantity ────────────────────────────────────────────────────

/**
 * Adjust the lifetime quantity cap on a discount. Allowed only while no sales
 * order has consumed any units — once consumption exists, the cap locks to
 * preserve the contract that approved orders can rely on.
 */
export const UpdateDiscountMaxQuantityInputSchema = z.object({
  id: UUIDSchema,
  maxQuantity: z
    .number()
    .positive("Max quantity must be greater than zero")
    .nullable(),
});

export const UpdateDiscountMaxQuantityResponseSchema = z.object({
  discount: DiscountSchema,
});

// ─── Disable discount (one-way) ─────────────────────────────────────────────

export const DisableDiscountInputSchema = z.object({
  id: UUIDSchema,
});

export const DisableDiscountResponseSchema = z.object({
  id: UUIDSchema,
  isActive: z.literal(false),
});

// ─── Discount approval actions ───────────────────────────────────────────────

/**
 * Approval decision enum matching backend model
 */
export const ApprovalDecisionSchema = z.enum([
  "APPROVED",
  "REJECTED",
  "REQUESTED_CHANGES",
]);

/**
 * Submit approval decision input schema
 */
export const SubmitApprovalDecisionInputSchema = z.object({
  discountId: UUIDSchema,
  decision: ApprovalDecisionSchema,
  notes: z.string().optional(),
  /** If provided, signals the associated workflow to recheck pricing after the decision */
  stagedPurchaseOrderId: z.string().uuid().optional(),
});

/**
 * Submit approval decision response schema
 */
export const SubmitApprovalDecisionResponseSchema = z.object({
  success: z.boolean(),
  /** Whether the discount is now fully approved (all levels complete) */
  isFullyApproved: z.boolean(),
  /** Current approval level after this decision */
  currentLevel: z.number(),
  /** Total approval levels required */
  requiredLevel: z.number(),
  /** New approval status after this decision */
  approvalStatus: z.enum(["PENDING", "APPROVED", "REJECTED"]),
});

// ─── Bulk approval decision ─────────────────────────────────────────────────

/**
 * Bulk submit approval decision input. Restricted to APPROVED — reject /
 * request-changes remain per-discount via submitApprovalDecision. One shared
 * `notes` value is applied to every decision.
 */
export const SubmitApprovalDecisionBulkInputSchema = z.object({
  discountIds: z.array(UUIDSchema).min(1, "At least one discount is required"),
  notes: z.string().optional(),
  stagedPurchaseOrderId: UUIDSchema.optional(),
});

export const SubmitApprovalDecisionBulkResultSchema = z.object({
  discountId: UUIDSchema,
  status: z.enum(["approved", "failed"]),
  isFullyApproved: z.boolean().optional(),
  currentLevel: z.number().optional(),
  requiredLevel: z.number().optional(),
  error: z.string().optional(),
});

export const SubmitApprovalDecisionBulkResponseSchema = z.object({
  results: z.array(SubmitApprovalDecisionBulkResultSchema),
});

// ─── Bulk approval eligibility (pre-check for the UI) ──────────────────────

/**
 * Ask "of these pending-approval discounts, which ones can the current user
 * approve at the next level?" Backs the Bulk approve button visibility so
 * L1-only approvers don't see a button that would fail for every row.
 */
export const GetBulkApprovalEligibilityInputSchema = z.object({
  discountIds: z.array(UUIDSchema).min(1),
});

export const GetBulkApprovalEligibilityResponseSchema = z.object({
  /** Subset of the input discountIds that the user can advance next level on. */
  eligibleDiscountIds: z.array(UUIDSchema),
});

// ─── Bulk create discounts ──────────────────────────────────────────────────

/**
 * One row in a bulk create — represents a single line item on the order
 * that should receive its own discount. Per-line `value` is computed by the
 * backend from `poUnitPrice` so the frontend just forwards what it has.
 */
export const BulkCreateDiscountItemSchema = z.object({
  productVariantId: UUIDSchema,
  customerId: UUIDSchema,
  sku: z.string().min(1),
  poUnitPrice: z.number().nonnegative(),
  listPrice: z.number().positive().optional(),
  name: z.string().min(1),
});

/**
 * Bulk create discounts input — shared inputs plus per-line items.
 */
export const BulkCreateDiscountsInputSchema = z
  .object({
    discountType: DiscountTypeSchema,
    effectiveStart: DateOnlySchema,
    effectiveEnd: DateOnlySchema,
    explanation: z
      .string()
      .min(10, "Explanation must be at least 10 characters"),
    poNumber: z.string().max(255).nullable().optional(),
    /**
     * Shared reference code applied to every discount created in the batch.
     * Callers that mean "no code" must send `null` or omit the field — empty
     * strings are rejected so we don't silently launder `""` into `null`.
     */
    referenceCode: z.string().trim().min(1).max(255).nullable().optional(),
    attachment: AttachmentUrlSchema,
    /** When true, every created discount is single-use (PO-specific). Bulk
     *  rows are always SKU-scoped + customer-specific, so this is safe. */
    isSingleUse: z.boolean().optional(),
    stagedPurchaseOrderId: UUIDSchema.optional(),
    items: z.array(BulkCreateDiscountItemSchema).min(1),
  })
  .superRefine((data, ctx) => {
    if (data.effectiveStart && data.effectiveEnd) {
      const start = new Date(data.effectiveStart);
      const end = new Date(data.effectiveEnd);
      if (end < start) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "End date must be on or after start date",
          path: ["effectiveEnd"],
        });
      }
    }
  });

export const BulkCreateDiscountResultSchema = z.object({
  productVariantId: UUIDSchema,
  sku: z.string(),
  status: z.enum(["created", "failed"]),
  discountId: UUIDSchema.optional(),
  error: z.string().optional(),
});

export const BulkCreateDiscountsResponseSchema = z.object({
  results: z.array(BulkCreateDiscountResultSchema),
});

/**
 * Get discount approval status input schema
 */
export const GetApprovalStatusInputSchema = z.object({
  discountId: UUIDSchema,
});

/**
 * Approval decision record schema
 */
export const ApprovalDecisionRecordSchema = z.object({
  id: UUIDSchema,
  level: z.number(),
  levelName: z.string().nullable(),
  decision: ApprovalDecisionSchema,
  decidedByUserId: UUIDSchema.nullable(),
  decidedByUserName: z.string().nullable(),
  notes: z.string().nullable(),
  decidedAt: z.coerce.date(),
});

/**
 * Get discount approval status response schema
 */
export const GetApprovalStatusResponseSchema = z.object({
  discountId: UUIDSchema,
  approvalStatus: z.enum(["PENDING", "APPROVED", "REJECTED"]).nullable(),
  currentLevel: z.number().nullable(),
  requiredLevel: z.number().nullable(),
  matchedRuleName: z.string().nullable(),
  /** Name of the next approval level (e.g., "Remittance / Finance") when status is PENDING */
  nextLevelName: z.string().nullable(),
  /** Whether the current user is authorized to approve the next level */
  canApproveNextLevel: z.boolean(),
  decisions: z.array(ApprovalDecisionRecordSchema),
});

/**
 * Filter options for discount dropdowns (categories + product lines)
 */
export const DiscountFilterOptionsResponseSchema = z.object({
  categories: z.array(z.string()),
  productLines: z.array(z.string()),
});

/**
 * Search scopes for discount dropdowns (SKU + customer search-as-you-type)
 */
export const SearchDiscountScopesInputSchema = z.object({
  query: z.string().trim().default(""),
  type: z.enum(["sku", "customer", "endCustomer"]),
  limit: z.number().int().min(1).max(50).optional(),
});

export const SearchDiscountScopesResponseSchema = z.object({
  results: z.array(
    z.object({
      id: z.string(),
      label: z.string(),
    }),
  ),
});

// ─── Get Documents ─────────────────────────────────────────────────────────

export const GetDiscountDocumentsInputSchema = z.object({
  discountId: UUIDSchema,
});

export const DiscountDocumentSchema = z.object({
  id: UUIDSchema,
  name: z.string(),
  mimeType: z.string().nullable(),
  fileSizeBytes: z.number().nullable(),
  createdAt: z.coerce.date(),
});

export const GetDiscountDocumentsResponseSchema = z.object({
  documents: z.array(DiscountDocumentSchema),
});

/**
 * Discounts contracts
 */
export const discountsContracts = {
  list: oc
    .input(ListDiscountsInputSchema)
    .output(ListDiscountsResponseSchema)
    .route({
      summary: "List legacy discounts",
      description:
        "Search legacy customer discounts with their scopes and approval status, filtered by type, active state, dates, customer, SKU, category, product line or end customer. Paginated.",
      method: "GET",
    }),
  create: oc
    .input(CreateDiscountInputSchema)
    .output(CreateDiscountResponseSchema)
    .route({
      summary: "Create a legacy discount",
      description:
        "Create a percent or fixed-price discount with customer/product scopes, starting multi-level approval when rules require it. Optionally signals a staged PO's workflow to recheck pricing.",
      method: "POST",
    }),
  getListPrices: oc
    .input(GetListPricesInputSchema)
    .output(GetListPricesResponseSchema)
    .route({
      summary: "Get list prices for SKUs",
      description:
        "Look up a customer's ERP list price, currency, price list and unit of measure for up to 500 SKUs. Returns empty prices if the ERP is unavailable.",
      method: "POST",
    }),
  getAvailableCategories: oc
    .output(GetAvailableCategoriesResponseSchema)
    .route({
      summary: "List product categories and product lines",
      description:
        "Return every product category and product line in the catalog, for choosing a discount's category or line target.",
      method: "GET",
      path: "/categories",
    }),
  getProductDiscountGroups: oc
    .output(GetProductDiscountGroupsResponseSchema)
    .route({
      summary: "List product discount groups",
      description:
        "Return the organization's product discount groups with descriptions and product counts, for targeting a discount at a named group.",
      method: "GET",
      path: "/product-discount-groups",
    }),
  delete: oc
    .input(DeleteDiscountInputSchema)
    .output(DeleteDiscountResponseSchema)
    .route({
      summary: "Delete a legacy discount",
      description:
        "Admin-only: soft-delete a discount and all its scopes. Non-admins should disable the discount instead.",
      method: "DELETE",
    }),
  disable: oc
    .input(DisableDiscountInputSchema)
    .output(DisableDiscountResponseSchema)
    .route({
      summary: "Disable a legacy discount",
      description:
        "Permanently deactivate a discount; it cannot be reactivated, so a new discount must be created. Idempotent if already disabled.",
      method: "POST",
      path: "/disable",
    }),
  updateMaxQuantity: oc
    .input(UpdateDiscountMaxQuantityInputSchema)
    .output(UpdateDiscountMaxQuantityResponseSchema)
    .route({
      summary: "Change a discount's quantity cap",
      description:
        "Set or clear a discount's lifetime maximum quantity. Refused once any sales order has consumed units against it. Returns the updated discount.",
      method: "POST",
      path: "/update-max-quantity",
    }),
  submitApprovalDecision: oc
    .input(SubmitApprovalDecisionInputSchema)
    .output(SubmitApprovalDecisionResponseSchema)
    .route({
      summary: "Approve or reject a discount",
      description:
        "Record an approve, reject or request-changes decision with notes on a pending discount, advancing approval levels; re-evaluates waiting orders and optionally signals a staged PO's workflow.",
      method: "POST",
      path: "/approval/decision",
    }),
  submitApprovalDecisionBulk: oc
    .input(SubmitApprovalDecisionBulkInputSchema)
    .output(SubmitApprovalDecisionBulkResponseSchema)
    .route({
      summary: "Bulk approve discounts",
      description:
        "Approve many pending discounts at once with shared notes; each succeeds or fails independently and per-discount results return. Optionally signals a staged PO's workflow to recheck.",
      method: "POST",
      path: "/approval/decision/bulk",
    }),
  getBulkApprovalEligibility: oc
    .input(GetBulkApprovalEligibilityInputSchema)
    .output(GetBulkApprovalEligibilityResponseSchema)
    .route({
      summary: "Check which discounts I can approve",
      description:
        "Given pending discount IDs, return the subset the current user is authorized to approve at the next approval level.",
      method: "POST",
      path: "/approval/bulk-eligibility",
    }),
  createBulk: oc
    .input(BulkCreateDiscountsInputSchema)
    .output(BulkCreateDiscountsResponseSchema)
    .route({
      summary: "Bulk create discounts for order lines",
      description:
        "Create one SKU- and customer-scoped discount per order line from shared dates, type and explanation, with values derived from PO unit prices. Returns per-line results.",
      method: "POST",
      path: "/bulk",
    }),
  getApprovalStatus: oc
    .input(GetApprovalStatusInputSchema)
    .output(GetApprovalStatusResponseSchema)
    .route({
      summary: "Get a discount's approval status",
      description:
        "Return a discount's approval status, current and required levels, matched rule, next level name, whether you can approve it, and the decision history.",
      method: "GET",
      path: "/approval/status",
    }),
  filterOptions: oc.output(DiscountFilterOptionsResponseSchema).route({
    summary: "List discount filter categories and lines",
    description:
      "Return the distinct product categories and product lines used on existing discount scopes, for list-page filter dropdowns.",
    method: "GET",
    path: "/filter-options",
  }),
  searchScopes: oc
    .input(SearchDiscountScopesInputSchema)
    .output(SearchDiscountScopesResponseSchema)
    .route({
      summary: "Search discount SKUs or customers",
      description:
        "Search-as-you-type over SKUs, customers or end customers that appear on the organization's discounts. Returns up to 50 id/label matches.",
      method: "GET",
      path: "/search-scopes",
    }),
  getDocuments: oc
    .input(GetDiscountDocumentsInputSchema)
    .output(GetDiscountDocumentsResponseSchema)
    .route({
      summary: "List documents attached to a discount",
      description:
        "Return the attachment documents (name, type, size, date) for a legacy discount; empty when the discount is not found.",
      method: "GET",
      path: "/documents",
    }),
} as const;

// Type exports
export type DiscountType = z.infer<typeof DiscountTypeSchema>;
export type DiscountTargetType = z.infer<typeof DiscountTargetTypeSchema>;
export type Discount = z.infer<typeof DiscountSchema>;
export type DiscountScope = z.infer<typeof DiscountScopeSchema>;
export type DiscountUser = z.infer<typeof DiscountUserSchema>;
export type ListDiscountsInput = z.infer<typeof ListDiscountsInputSchema>;
export type ListDiscountsResponse = z.infer<typeof ListDiscountsResponseSchema>;
export type CreateDiscountScopeInput = z.infer<
  typeof CreateDiscountScopeInputSchema
>;
export type CreateDiscountInput = z.infer<typeof CreateDiscountInputSchema>;
export type CreateDiscountResponse = z.infer<
  typeof CreateDiscountResponseSchema
>;
export type ListPriceResult = z.infer<typeof ListPriceResultSchema>;
export type GetListPricesInput = z.infer<typeof GetListPricesInputSchema>;
export type GetListPricesResponse = z.infer<typeof GetListPricesResponseSchema>;
export type GetAvailableCategoriesResponse = z.infer<
  typeof GetAvailableCategoriesResponseSchema
>;
export type ProductDiscountGroup = z.infer<typeof ProductDiscountGroupSchema>;
export type GetProductDiscountGroupsResponse = z.infer<
  typeof GetProductDiscountGroupsResponseSchema
>;
export type DeleteDiscountInput = z.infer<typeof DeleteDiscountInputSchema>;
export type DeleteDiscountResponse = z.infer<
  typeof DeleteDiscountResponseSchema
>;
export type DisableDiscountInput = z.infer<typeof DisableDiscountInputSchema>;
export type DisableDiscountResponse = z.infer<
  typeof DisableDiscountResponseSchema
>;
export type ApprovalDecision = z.infer<typeof ApprovalDecisionSchema>;
export type SubmitApprovalDecisionInput = z.infer<
  typeof SubmitApprovalDecisionInputSchema
>;
export type SubmitApprovalDecisionResponse = z.infer<
  typeof SubmitApprovalDecisionResponseSchema
>;
export type SubmitApprovalDecisionBulkInput = z.infer<
  typeof SubmitApprovalDecisionBulkInputSchema
>;
export type SubmitApprovalDecisionBulkResult = z.infer<
  typeof SubmitApprovalDecisionBulkResultSchema
>;
export type SubmitApprovalDecisionBulkResponse = z.infer<
  typeof SubmitApprovalDecisionBulkResponseSchema
>;
export type GetBulkApprovalEligibilityInput = z.infer<
  typeof GetBulkApprovalEligibilityInputSchema
>;
export type GetBulkApprovalEligibilityResponse = z.infer<
  typeof GetBulkApprovalEligibilityResponseSchema
>;
export type BulkCreateDiscountItem = z.infer<
  typeof BulkCreateDiscountItemSchema
>;
export type BulkCreateDiscountsInput = z.infer<
  typeof BulkCreateDiscountsInputSchema
>;
export type BulkCreateDiscountResult = z.infer<
  typeof BulkCreateDiscountResultSchema
>;
export type BulkCreateDiscountsResponse = z.infer<
  typeof BulkCreateDiscountsResponseSchema
>;
export type GetApprovalStatusInput = z.infer<
  typeof GetApprovalStatusInputSchema
>;
export type ApprovalDecisionRecord = z.infer<
  typeof ApprovalDecisionRecordSchema
>;
export type GetApprovalStatusResponse = z.infer<
  typeof GetApprovalStatusResponseSchema
>;
export type DiscountFilterOptionsResponse = z.infer<
  typeof DiscountFilterOptionsResponseSchema
>;
export type SearchDiscountScopesInput = z.infer<
  typeof SearchDiscountScopesInputSchema
>;
export type SearchDiscountScopesResponse = z.infer<
  typeof SearchDiscountScopesResponseSchema
>;
export type DiscountDocument = z.infer<typeof DiscountDocumentSchema>;
export type GetDiscountDocumentsResponse = z.infer<
  typeof GetDiscountDocumentsResponseSchema
>;
